import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

const SECTION = 'cart-drawer-section';
const SP_VARIANT = '54552873828670';

/**
 * Every cart write that this file makes goes through one queue so requests never overlap or race
 * (this is what caused flicker / double adds with the free gift and Shipping Protection).
 */
let chain = Promise.resolve();
let pending = 0;

/**
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function enqueue(task) {
  pending += 1;
  const run = chain.then(() => task());
  chain = run
    .catch(() => {})
    .then(() => {
      pending -= 1;
    });
  return run;
}

/** Header bubble should count products only (not the protection line). */
function syncBubble(count) {
  const fix = () => /** @type {any} */ (document.querySelector('cart-icon'))?.renderCartBubble?.(count, false);
  setTimeout(fix, 60);
  setTimeout(fix, 400);
}

/**
 * One request: /cart/update.js changes lines (and optionally cart attributes),
 * then the existing cart components morph the drawer from the returned section HTML.
 * @param {Record<string, number>} updates variantId (or line key) -> quantity
 * @param {Record<string, string> | null} attributes
 * @param {EventTarget} target
 * @returns {Promise<boolean>}
 */
function cartUpdate(updates, attributes, target) {
  return enqueue(async () => {
    /** @type {Record<string, unknown>} */
    const body = { updates, sections: SECTION, sections_url: window.location.pathname };
    if (attributes) body.attributes = attributes;

    const deferred = CartLinesUpdateEvent.createPromise();
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await response.json();
      if (!response.ok || json.errors || json.status) throw new Error(json.description || 'Cart update failed');

      target.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
        detail: {
          sections: json.sections,
          items: json.items,
          itemCount: json.item_count,
          source: 'lt-ship-protect',
          didError: false,
        },
      });

      const others = (json.items || []).filter((/** @type {any} */ i) => String(i.variant_id) !== SP_VARIANT);
      syncBubble(others.reduce((/** @type {number} */ n, /** @type {any} */ i) => n + i.quantity, 0));
      return true;
    } catch (error) {
      deferred.reject(error);
      return false;
    }
  });
}

/**
 * @param {number} qty
 * @param {string | null} optOut
 * @param {EventTarget} target
 */
function updateProtection(qty, optOut, target) {
  return cartUpdate({ [SP_VARIANT]: qty }, optOut === null ? null : { lt_sp_off: optOut }, target);
}

class LtShipProtect extends HTMLElement {
  static observedAttributes = ['data-other', 'data-sp-qty'];

  connectedCallback() {
    this.input = this.querySelector('.lt-sp__input');
    this.input?.addEventListener('change', this.onToggle);

    const other = Number(this.dataset.other || 0);
    const inCart = this.dataset.inCart === 'true';
    const optedOut = this.dataset.optedOut === 'true';

    syncBubble(other);

    if (pending > 0) return;
    if (Number(this.dataset.spQty || 0) > 1) updateProtection(1, null, this);
    else if (other === 0 && inCart) updateProtection(0, null, this);
    else if (other > 0 && !inCart && !optedOut) updateProtection(1, null, this);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('change', this.onToggle);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue) return;
    if (name === 'data-other') syncBubble(Number(newValue || 0));
    // Never let the protection line go above 1.
    if (name === 'data-sp-qty' && Number(newValue) > 1 && pending === 0) updateProtection(1, null, this);
  }

  /** Update the shown total instantly; the server render then confirms it. */
  optimisticTotal(on) {
    try {
      const cents = Number(this.dataset.priceCents || 0);
      const el = document.querySelector('#cart-drawer [data-cart-subtotal]');
      if (!cents || !el) return;
      const text = el.textContent.trim();
      const current = parseFloat(text.replace(/[^0-9.]/g, ''));
      if (Number.isNaN(current)) return;
      const next = Math.max(0, current + (on ? cents : -cents) / 100);
      const suffix = /AUD/.test(text) ? ' AUD' : '';
      el.textContent = '$' + next.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + suffix;
    } catch (_) {
      /* ignore: the server render will correct it */
    }
  }

  onToggle = async () => {
    if (!this.input) return;
    const on = this.input.checked;
    this.optimisticTotal(on);
    const ok = await updateProtection(on ? 1 : 0, on ? '' : '1', this);
    if (!ok) this.input.checked = !on;
  };
}

if (!customElements.get('lt-ship-protect')) {
  customElements.define('lt-ship-protect', LtShipProtect);
}

/** "Complete the look" upsell card: pick an option, tap Add (instant feedback, then one request). */
class LtUpsell extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('.lt-up__select');
    this.button = this.querySelector('.lt-up__add');
    this.select?.addEventListener('change', this.onSelect);
    this.button?.addEventListener('click', this.onAdd);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.onSelect);
    this.button?.removeEventListener('click', this.onAdd);
  }

  onSelect = () => {
    const option = this.select?.selectedOptions[0];
    if (!option) return;
    const now = this.querySelector('.lt-up__now');
    const was = this.querySelector('.lt-up__was');
    if (now) now.textContent = option.dataset.price || '';
    if (was) was.textContent = option.dataset.was || '';
  };

  onAdd = async () => {
    if (!this.button || this.button.disabled) return;
    const id = this.select ? this.select.value : this.button.dataset.variant;
    if (!id) return;
    this.button.disabled = true;
    this.button.textContent = 'Added';
    const ok = await cartUpdate({ [id]: 1 }, null, this);
    if (!ok) {
      this.button.disabled = false;
      this.button.textContent = 'Add';
    }
  };
}

if (!customElements.get('lt-upsell')) {
  customElements.define('lt-upsell', LtUpsell);
}

/**
 * Free gift at the spend threshold. The auto-added line is tracked with the cart attribute
 * _lt_gift: '1' = auto-added, 'off' = the shopper removed it (don't add it again), '' = not set,
 * 'skip-<cart key>' = we took it out because it would have been charged (see below).
 * If the attribute is '1' but the gift is no longer in the cart while the cart still qualifies,
 * the shopper removed it, so we record 'off' instead of re-adding it.
 *
 * Never charged (29 Oct 2026): Shopify gives the same items either the free gift or the multi-buy discount,
 * whichever saves more. When the multi-buy wins, the auto-added earrings would be charged at full price.
 * data-charged: the auto-added earrings are being charged, so take them out.
 * data-skip: the multi-buy is worth more (or we already took them out for this exact cart), so don't add them.
 * Both are worked out in snippets/lt-free-ship-bar.liquid. Earrings the shopper adds themselves are never touched.
 */
class LtFreeGift extends HTMLElement {
  static observedAttributes = ['data-eligible', 'data-in-cart', 'data-auto', 'data-opted-out', 'data-charged', 'data-skip'];

  connectedCallback() {
    this.sync();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue) return;
    this.sync();
  }

  /** @param {number} [attempt] */
  sync(attempt = 0) {
    if (pending > 0) {
      if (attempt < 25) setTimeout(() => this.isConnected && this.sync(attempt + 1), 250);
      return;
    }
    const eligible = this.dataset.eligible === 'true';
    const inCart = this.dataset.inCart === 'true';
    const auto = this.dataset.auto === 'true';
    const optedOut = this.dataset.optedOut === 'true';
    const charged = this.dataset.charged === 'true';
    const skip = this.dataset.skip === 'true';
    const key = String(this.dataset.sig || '');
    const variantId = String(this.dataset.variantId || '');
    if (!variantId) return;

    if (eligible && inCart && auto && charged) {
      cartUpdate({ [variantId]: 0 }, { _lt_gift: 'skip-' + key }, this);
    } else if (eligible && !inCart && !optedOut && !auto && !skip) {
      cartUpdate({ [variantId]: 1 }, { _lt_gift: '1' }, this);
    } else if (eligible && !inCart && auto) {
      cartUpdate({}, { _lt_gift: 'off' }, this);
    } else if (!eligible && inCart && auto) {
      cartUpdate({ [variantId]: 0 }, { _lt_gift: '' }, this);
    } else if (!eligible && !inCart && (auto || optedOut)) {
      cartUpdate({}, { _lt_gift: '' }, this);
    }
  }
}

if (!customElements.get('lt-free-gift')) {
  customElements.define('lt-free-gift', LtFreeGift);
}

// If the last real product is removed, drop the protection line too (so the cart truly empties).
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const e = /** @type {any} */ (event);
  e.promise
    ?.then(({ detail }) => {
      const items = detail?.items;
      if (!Array.isArray(items) || pending > 0 || detail?.source === 'lt-ship-protect') return;
      if (items.length > 0 && items.every((i) => String(i.variant_id) === SP_VARIANT)) {
        updateProtection(0, null, document);
      }
    })
    .catch(() => {});
});

// Instant feedback: the moment a shopper taps remove, the row disappears (the cart request runs in the background).
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('.cart-items__remove');
    const row = btn && btn.closest('.cart-items__table-row');
    if (row) row.classList.add('lt-row-removing');
  },
  true
);

// Instant quantity buttons: the number changes the moment + or - is tapped, the request runs in the background.
window.addEventListener(
  ThemeEvents.quantitySelectorUpdate,
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const cart = /** @type {any} */ (target.closest('cart-items-component'));
    if (!cart || typeof cart.updateQuantity !== 'function') return;
    const { quantity, cartLine: line } = /** @type {any} */ (event).detail || {};
    if (!line) return;

    event.stopPropagation();
    if (quantity === 0) {
      cart.onLineItemRemove(line);
      return;
    }
    const input = target.querySelector('input');
    if (input) /** @type {HTMLInputElement} */ (input).value = String(quantity);
    cart.updateQuantity({ line, quantity, action: 'change' });
    cart.refs?.cartItemRows?.[line - 1]?.querySelector('text-component')?.shimmer?.();
  },
  true
);
